import { Injectable } from '@angular/core';
import { LoadingController, ToastController } from '@ionic/angular';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MissionsService } from './missions.service';
import { SERVER_URL } from 'src/environments/environment';
import { FileInput, FileValidator } from 'ngx-material-file-input';

@Injectable({
  providedIn: 'root'
})
export class ToolsService {
  months = [
    { name: "Enero", value: 1 },
    { name: "Febrero", value: 2 },
    { name: "Marzo", value: 3 },
    { name: "Abril", value: 4 },
    { name: "Mayo", value: 5 },
    { name: "Junio", value: 6 },
    { name: "Julio", value: 7 },
    { name: "Agosto", value: 8 },
    { name: "Septiembre", value: 9 },
    { name: "Octubre", value: 10 },
    { name: "Noviembre", value: 11 },
    { name: "Diciembre", value: 12 },
  ]

  years = [
    "2022",
    "2023",
    "2024",
    "2025",
    "2026"
  ]
  //Navigation
  breadcrumbs: any = []

  maxFileSize = 2097152 // it's 2 MB (=2 * 2 ** 20).
  
  termAndConditions = true;

  constructor(
    private loadingController: LoadingController,
    private toastCtrl: ToastController,
    private missionsService: MissionsService
  ) {
  }

  //Function to reset breadcrumbs
  resetBreadCrumbs() {
    this.breadcrumbs = []
  }

  async showLoader(message = 'Cargando...') {
    const loading = await this.loadingController.create({
      message
    });
    return await loading.present();
  }


  async dismissLoader() {
    return await this.loadingController.dismiss()
  }

  async getTopLoader() {
    return await this.loadingController.getTop()
  }


  async showErrorToast(msg = 'Error inesperado, verifica tu conexión e intenta de nuevo más tarde') {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 5000,
      color: 'danger',
      position: 'top',
      buttons: [
        {
          text: 'X',
          role: 'cancel',
          handler: () => { }
        }
      ]
    });
    toast.present();
  }


  async showSuccessToast(msg) {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 5000,
      color: 'success',
      position: 'top',
      buttons: [
        {
          text: 'X',
          role: 'cancel',
          handler: () => { }
        }
      ]
    });
    toast.present();
  }

  //Function to format date as DD-MM-AAAA
  formatDate(date) {
    var aux_date = new Date(date)
    var DD = aux_date.getDate() + ''
    if (DD.length == 1)
      DD = '0' + DD
    var MM = (aux_date.getMonth() + 1) + ''
    if (MM.length == 1)
      MM = '0' + MM
    return DD + '-' + MM + '-' + aux_date.getFullYear();
  }

  customRegex(nameRe: string, validator_regex_error_message?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let reg = new RegExp(nameRe)
      const isValid = reg.test(control.value);
      if(!validator_regex_error_message) validator_regex_error_message = "El dato ingresado no es correcto";
      return isValid ? null : { customRegex: { value: control.value, msg: validator_regex_error_message } };
    };
  }

  //Function to format the questions
  async formatQuestions(questions) {
    var fixedQuestions = []
    for (let question of questions) {
      //Generic case
      let fixedQuestion = { id: question.id, label: question.label, format: question.question_type, type: 'text', formControl: undefined, options: question.options, src: [], multiple_response: question.multiple_response, filtered_options: [] };
      var validators = []

      //Special cases
      if (question.question_type == 'text') {
        fixedQuestion.format = 'input'
        // Add specific pattern validator from question
        if (question.validator_regex) {
          validators.push(this.customRegex(question.validator_regex, question.validator_regex_error_message))
        }
      }
      else if (question.question_type == 'number' || question.question_type == 'float' || question.question_type == 'amount') {
        fixedQuestion.format = 'input';
        fixedQuestion.type = 'number';
      } else if (question.question_type == 'product') {
        fixedQuestion.format = 'autocomplete';
        fixedQuestion['search_url'] = '/missions/mission_products';
        fixedQuestion.options = [];
      }
      if (question.options.search_url) {
        fixedQuestion.options = []
        fixedQuestion['search_url'] = question.options.search_url
      }
      if (question.options.list_url) {
        var options = await this.missionsService.getOptionsDjango(question.options.list_url);
        fixedQuestion.options = options;
      }
      if (question.required)
        validators.push(Validators.required)
      if (question.question_type == 'number')
        validators.push(Validators.pattern(/^[0-9]+$/))
      if (question.question_type == 'file')
        validators.push(FileValidator.maxContentSize(this.maxFileSize))
      fixedQuestion.formControl = new FormControl('', validators)
      if (question.group) {
        var fixedGroup = fixedQuestions.find(item => item.id == question.group.id)
        if (fixedGroup)
          fixedGroup.questions.push(fixedQuestion)
        else {
          fixedGroup = { id: question.group.id, format: 'group', questions: [fixedQuestion], multiple_response: question.group.multiple_response }
          fixedQuestions.push(fixedGroup)
        }
      } else {
        fixedQuestions.push(fixedQuestion)
      }
    }
    return fixedQuestions;
  }


  //Function that returns true if no question has errors
  validateQuestions(questions) {
    var is_valid = true;
    for (let question of questions) {
      if (question.is_active === false) continue;
      if (question.format == 'autocomplete') {
        if (typeof question.formControl.value != "object") {
          question.formControl.setErrors({ 'notObject': true });
        }
      }
      if (question.format == 'group') is_valid = this.validateQuestions(question.questions);
      else if (question.formControl.errors) is_valid = false;
      if (!is_valid) break;
    }
    return is_valid;
  }

  markAsTouched(questions) {
    for (let question of questions) {
      if (question.format == 'group')
        this.markAsTouched(question.questions)
      else
        question.formControl.markAsTouched()
    }
  }

  //Function to get selected option in select field
  getSelectedOption(question) {
    return question.options.find(item => item.value == question.formControl.value)
  }


  formatCurrency(value) {
    const options = {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    };
    const numberFormat = new Intl.NumberFormat('en-US', options);
    return numberFormat.format(value);
  }

  displayDateInDays(end: string) {
    let actual_date = new Date();
    let end_date = new Date(end);
    let diff_time = end_date.getTime() - actual_date.getTime();
    let diff_days = Math.abs(diff_time / (1000 * 3600 * 24));
    return Math.ceil(diff_days);

  }

  specialCharactersValidator(control: FormControl): { [key: string]: boolean } {
    const nameRegexp: RegExp = /[¡!@$%*={};':",\/¿?ç]/;
    if (control.value && nameRegexp.test(control.value)) {
      return { invalidName: true };
    }
  }


  formatGoalName(goal_name) {
    // if (/\d/img.test(goal_name))
    //   goal_name = goal_name.replace(/\d/img, "")
    return goal_name
  }

  optimizeProfileImage(originalImg) {
    return new Promise(async function (resolve) {
      var img = document.createElement('img');
      img.onload = function () {
        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, img.width, img.height);
        var dataURI = canvas.toDataURL('image/jpeg', 0.8);
        resolve(dataURI);
      };
      img.src = originalImg;
    })
  }

}
